import { Injectable } from '@angular/core';
import { CalendarEvent, ServiceRequestModel } from '@app/core/models';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { ServiceRequestStatus } from '@app/shared/enums/service-requests-status.enum';

import { createStore, setProps, withProps } from '@ngneat/elf';
import {
  deleteAllEntities,
  selectAllEntities,
  selectEntity,
  selectManyByPredicate,
  updateEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { joinRequestResult } from '@ngneat/elf-requests';
import { endOfDay, isAfter, startOfDay } from 'date-fns';
import { Observable, map, tap } from 'rxjs';

export interface ServiceRequestsProps {
  meta: {
    lastRefresh: Date | null;
  };
}

const store = createStore(
  { name: 'serviceRequests' },
  withProps<ServiceRequestsProps>({
    meta: { lastRefresh: null },
  }),
  withEntities<ServiceRequestModel, '_id'>({ idKey: '_id' }),
);

@Injectable({ providedIn: 'root' })
export class ServiceRequestsRepository {
  public serviceRequests$ = store.pipe(
    selectAllEntities(),
    joinRequestResult(['serviceRequests']),
    tap((serviceRequests) => {
      LoggingService.log('service requests', serviceRequests);
    }),
  );

  public activeVouchers$ = store.pipe(
    selectManyByPredicate((serviceRequest) => {
      return (
        !!serviceRequest &&
        serviceRequest.status === ServiceRequestStatus.Confirmed &&
        (serviceRequest.quota || 0) > 0 &&
        isAfter(new Date(serviceRequest.expirationDate || ''), new Date()) &&
        serviceRequest.service?.serviceType === 'voucher'
      );
    }),
  );

  public upcomingToday$ = store.pipe(
    selectAllEntities(),
    map((serviceRequests) => {
      const now = new Date();
      const endOfToday = endOfDay(now);

      return serviceRequests.filter((serviceRequest) => {
        if (!serviceRequest?.service || !serviceRequest?.date?.from) {
          return false;
        }
        const fromDate = new Date(serviceRequest.date.from);
        return (
          serviceRequest.status !== ServiceRequestStatus.Canceled &&
          serviceRequest.service.serviceType !== 'voucher' &&
          fromDate >= now &&
          fromDate <= endOfToday
        );
      });
    }),
  );

  public upcomingTodayCount$ = this.upcomingToday$.pipe(
    map((serviceRequests) => serviceRequests.length),
  );

  public getById(_id: string) {
    return store.pipe(selectEntity(_id));
  }

  public calendarEvents$: Observable<CalendarEvent[]> = store.pipe(
    selectAllEntities(),
    map((serviceRequests) => {
      if (serviceRequests && serviceRequests.length > 0) {
        return (serviceRequests as ServiceRequestModel[])
          .filter(
            (serviceRequest: ServiceRequestModel) =>
              !!serviceRequest?.service &&
              serviceRequest.status !== ServiceRequestStatus.Canceled &&
              serviceRequest.service.serviceType !== 'voucher',
          )
          .map((serviceRequest: ServiceRequestModel) => ({
            title: serviceRequest.service.name,
            start: serviceRequest.date.from,
            end: serviceRequest.date.to,
            className: serviceRequest.status,
            allDay: false,
            extendedProps: {
              serviceRequest,
            },
          }));
      } else {
        return [];
      }
    }),
  );

  public upcomingReservations(
    bookingId?: string,
  ): Observable<ServiceRequestModel[]> {
    return store.pipe(
      selectManyByPredicate((serviceRequest) => {
        return (
          !!serviceRequest?.service &&
          !!serviceRequest?.date?.from &&
          serviceRequest.status !== 'canceled' &&
          serviceRequest.service.serviceType !== 'voucher' &&
          new Date(serviceRequest.date.from) >= new Date()
        );
      }),
      map((serviceRequests) => {
        return serviceRequests.sort((a, b) => {
          return (
            new Date(a.date.from).getTime() - new Date(b.date.from).getTime()
          );
        });
      }),
    );
  }

  public upcomingVouchers(): Observable<ServiceRequestModel[]> {
    return store.pipe(
      selectManyByPredicate((serviceRequest) => {
        return (
          !!serviceRequest?.service &&
          serviceRequest.status !== 'canceled' &&
          serviceRequest.service.serviceType === 'voucher'
        );
      }),
    );
  }

  public getByDate(date: Date): Observable<ServiceRequestModel[]> {
    return store.pipe(
      selectManyByPredicate(
        (serviceRequest) =>
          !!serviceRequest?.service &&
          !!serviceRequest?.date?.from &&
          !!serviceRequest?.date?.to &&
          serviceRequest.service.serviceType !== 'voucher' &&
          ((new Date(serviceRequest.date.from) <= endOfDay(date) &&
            new Date(serviceRequest.date.from) >= startOfDay(date)) ||
            (new Date(serviceRequest.date.to) <= endOfDay(date) &&
              new Date(serviceRequest.date.to) >= startOfDay(date))) &&
          serviceRequest.status !== 'canceled',
      ),
      map((serviceRequests) => {
        // Sort all service requests by date
        return serviceRequests.sort(
          (a, b) =>
            new Date(a.date.from).getTime() - new Date(b.date.from).getTime(),
        );
      }),
    );
  }

  public createServiceRequests(serviceRequest: ServiceRequestModel): void {
    if (serviceRequest) {
      store.update(upsertEntities(serviceRequest));
    }
  }

  public updateServiceRequests(serviceRequests: ServiceRequestModel[]): void {
    store.update(
      upsertEntities(serviceRequests),
      setProps({ meta: { lastRefresh: new Date() } }),
    );
  }

  public updateServiceRequest(serviceRequest: ServiceRequestModel): void {
    store.update(updateEntities(serviceRequest._id, serviceRequest));
  }

  public clear(): void {
    store.update(deleteAllEntities());
    store.update(setProps({ meta: { lastRefresh: null } }));
  }
}
